// State machine for the dealer-org-admin "Locations" admin section.
// Mirrors useManufacturingPlantsTab's shape (listBusy / actionBusy /
// localError / creating / editingId / edit-draft) so the section
// component can stay declarative.
//
// Soft delete only: `toggleActive(loc)` flips `is_active`. There is
// no hard-delete path here on purpose — see locationsRepo.ts header.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createOrgLocationApi,
  listOrgLocationsApi,
  updateOrgLocationApi,
} from "@/lib/orgLocations/orgLocationsClient";
import type {
  OrgLocation,
  OrgLocationDraft,
  OrgLocationType,
} from "@/lib/orgLocations/orgLocationsTypes";

// HTML inputs give us strings; we coerce to OrgLocationDraft at
// submit time. `location_type` is a closed dropdown of the 4 valid
// values + an empty "(unspecified)" option.
export type OrgLocationFormDraft = {
  name: string;
  location_code: string;
  location_type: OrgLocationType | "";
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  phone: string;
  email: string;
  notes: string;
  is_public: boolean;
};

export const EMPTY_LOCATION_FORM: OrgLocationFormDraft = {
  name: "",
  location_code: "",
  location_type: "",
  address_1: "",
  address_2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "US",
  phone: "",
  email: "",
  notes: "",
  is_public: true,
};

function locationToForm(loc: OrgLocation): OrgLocationFormDraft {
  const lt = loc.location_type;
  const safeType: OrgLocationType | "" =
    lt === "retail" || lt === "plant" || lt === "corporate" || lt === "virtual"
      ? (lt as OrgLocationType)
      : "";
  return {
    name: loc.name ?? "",
    location_code: loc.location_code ?? "",
    location_type: safeType,
    address_1: loc.address_1 ?? "",
    address_2: loc.address_2 ?? "",
    city: loc.city ?? "",
    state: loc.state ?? "",
    postal_code: loc.postal_code ?? "",
    country: loc.country ?? "",
    phone: loc.phone ?? "",
    email: loc.email ?? "",
    notes: loc.notes ?? "",
    is_public: loc.is_public !== false,
  };
}

function formToDraft(form: OrgLocationFormDraft): OrgLocationDraft {
  const nullableTrim = (s: string): string | null => {
    const t = s.trim();
    return t.length === 0 ? null : t;
  };
  return {
    name: form.name.trim(),
    location_code: nullableTrim(form.location_code),
    location_type: form.location_type === "" ? null : form.location_type,
    address_1: nullableTrim(form.address_1),
    address_2: nullableTrim(form.address_2),
    city: nullableTrim(form.city),
    state: nullableTrim(form.state),
    postal_code: nullableTrim(form.postal_code),
    country: nullableTrim(form.country),
    phone: nullableTrim(form.phone),
    email: nullableTrim(form.email),
    notes: nullableTrim(form.notes),
    is_public: form.is_public,
  };
}

export type UseOrgLocationsTabOptions = {
  /** Fires after any successful mutation (create / update / toggle).
   *  Used by host panels that mirror the locations list elsewhere
   *  (e.g. the trainer Location-Tools dropdown). */
  onLocationsChanged?: () => void;
};

export type UseOrgLocationsTab = ReturnType<typeof useOrgLocationsTab>;

export function useOrgLocationsTab(opts: UseOrgLocationsTabOptions = {}) {
  const { onLocationsChanged } = opts;
  const [locations, setLocations] = useState<OrgLocation[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState<OrgLocationFormDraft>(EMPTY_LOCATION_FORM);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<OrgLocationFormDraft>(EMPTY_LOCATION_FORM);

  const reload = useCallback(async () => {
    setListBusy(true);
    setLocalError(null);
    const res = await listOrgLocationsApi({ includeInactive: showInactive });
    setListBusy(false);
    if (!res.ok) {
      setLocations([]);
      setLocalError(res.error);
      return;
    }
    setLocations(Array.isArray(res.data.locations) ? res.data.locations : []);
  }, [showInactive]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const startCreate = useCallback(() => {
    setCreating(true);
    setEditingId(null);
    setNewForm(EMPTY_LOCATION_FORM);
    setLocalError(null);
  }, []);

  const cancelCreate = useCallback(() => {
    setCreating(false);
    setLocalError(null);
  }, []);

  const submitCreate = useCallback(async () => {
    const name = newForm.name.trim();
    if (!name) {
      setLocalError("Name is required.");
      return;
    }
    setActionBusy(true);
    setLocalError(null);
    const res = await createOrgLocationApi(formToDraft(newForm));
    setActionBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return;
    }
    setCreating(false);
    setNewForm(EMPTY_LOCATION_FORM);
    await reload();
    onLocationsChanged?.();
  }, [newForm, reload, onLocationsChanged]);

  const startEdit = useCallback((loc: OrgLocation) => {
    setEditingId(loc.id);
    setCreating(false);
    setEditForm(locationToForm(loc));
    setLocalError(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setLocalError(null);
  }, []);

  const submitEdit = useCallback(async () => {
    if (!editingId) return;
    const name = editForm.name.trim();
    if (!name) {
      setLocalError("Name cannot be empty.");
      return;
    }
    setActionBusy(true);
    setLocalError(null);
    const res = await updateOrgLocationApi(editingId, formToDraft(editForm));
    setActionBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return;
    }
    setEditingId(null);
    await reload();
    onLocationsChanged?.();
  }, [editingId, editForm, reload, onLocationsChanged]);

  const toggleActive = useCallback(
    async (loc: OrgLocation) => {
      setActionBusy(true);
      setLocalError(null);
      const res = await updateOrgLocationApi(loc.id, { is_active: !loc.is_active });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      await reload();
      onLocationsChanged?.();
    },
    [reload, onLocationsChanged]
  );

  const visibleLocations = useMemo(() => {
    if (showInactive) return locations;
    return locations.filter((l) => l.is_active);
  }, [locations, showInactive]);

  return {
    locations,
    visibleLocations,
    listBusy,
    actionBusy,
    localError,
    showInactive,
    setShowInactive,
    creating,
    newForm,
    setNewForm,
    startCreate,
    cancelCreate,
    submitCreate,
    editingId,
    editForm,
    setEditForm,
    startEdit,
    cancelEdit,
    submitEdit,
    toggleActive,
    reload,
  };
}
